import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { Chat } from '../types/interfaces'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import { ChatStatus, UserRole } from '../types/enums'
import { parseUtcDate } from '../composables/useTimeFormatter'
import { MY_CHATS_TAB_ID } from '../services/constants'

const FINISHED_CHAT_STATUSES = [
  ChatStatus.FINISH,
  ChatStatus.AUTO_CLOSED,
  ChatStatus.ABANDON,
  ChatStatus.EXPIRE,
]

// Los chats que siguen vivos para el agente: los que la lista muestra. Lo que
// cae fuera (FINISH, AUTO_CLOSED) ya no admite mensajes.
export const ACTIVE_CHAT_STATUSES = [
  ChatStatus.BOT,
  ChatStatus.PENDING,
  ChatStatus.ATTENTION,
  ChatStatus.IN_PROGRESS,
  ChatStatus.TEMPLATE,
  ChatStatus.EXPIRE,
  ChatStatus.ABANDON,
]

// Los chats que el agente esta atendiendo: la pestana "Mis chats" y la insignia
// de no leidos se calculan con este mismo predicado para que no se separen.
export const MY_CHAT_STATUSES = [ChatStatus.ATTENTION, ChatStatus.IN_PROGRESS]

export const isMyChat = (chat: Pick<Chat, 'status'>) => MY_CHAT_STATUSES.includes(chat.status)

// Los chats de la cola: son de la campana, no de un agente, y los ve todo el
// equipo hasta que alguien los toma.
export const QUEUED_CHAT_STATUSES = [ChatStatus.PENDING, ChatStatus.ABANDON]

export const isQueuedChat = (chat: Pick<Chat, 'status'>) =>
  QUEUED_CHAT_STATUSES.includes(chat.status)

export type ChatSortDirection = 'asc' | 'desc'

const DEFAULT_SORT_DIRECTION: ChatSortDirection = 'desc'
const CHAT_SORT_STORAGE_PREFIX = 'whatsapp_chat_sort_direction:'

const isSortDirection = (value: unknown): value is ChatSortDirection =>
  value === 'asc' || value === 'desc'

interface ChatTranslationSetting {
  enabled: boolean
  agentLanguage: string
  clientLanguage: string
}

export const useChatStore = defineStore('chat', () => {
  const chats = ref<Chat[]>([])
  const selectedChatId = ref<string | null>(null)
  const currentUserId = ref<string | null>(null)
  const currentUserRole = ref<UserRole>(UserRole.OPERATOR)
  const campaignMap = ref<Map<string, IOptionsCampaign>>(new Map())
  const chatSignatureEnabled = ref<Record<string, boolean>>({})
  const chatTranslationSettings = ref<Record<string, ChatTranslationSetting>>({})
  const chatTimeoutClosures = ref<
    Record<string, { campaignId: string | null; botId: string | null }>
  >({})
  // chatId -> close_reason (BULK_TEMPLATE_REACTIVATION | TEMPLATE_TAKEOVER | TEMPLATE_HANDOFF)
  const chatBulkTemplateReleases = ref<Map<string, string>>(new Map())

  const selectedChat = computed(
    () => chats.value.find((chat) => chat.chat_id === selectedChatId.value) || null,
  )

  // El orden es por pestana: la cola se suele querer FIFO —el que mas lleva
  // esperando arriba— y los chats propios al reves. El boton actua sobre la
  // pestana visible, y cada una recuerda lo suyo. Por usuario, porque en el Oper
  // es normal que varios agentes se turnen el mismo equipo.
  const sortDirectionByTab = ref<Record<string, ChatSortDirection>>({})

  // Cuantas veces se completo la sincronizacion de la lista. `loadChats` la trae
  // en tandas de 30, asi que entre tanda y tanda faltan chats que si existen:
  // solo al terminar (aqui) la lista es autoritativa y se puede concluir que un
  // chat que no salio ya no esta.
  const fullSyncCount = ref(0)

  // Que pestana esta viendo el agente. Solo se pinta una a la vez, asi que
  // ordenar la lista con la direccion de esa pestana alcanza: las demas se
  // ordenan cuando se abren.
  const activeListTab = ref<string>(MY_CHATS_TAB_ID)

  const sortStorageKey = () => `${CHAT_SORT_STORAGE_PREFIX}${currentUserId.value || 'anonymous'}`

  const loadSortDirection = () => {
    try {
      const stored = localStorage.getItem(sortStorageKey())
      const parsed = stored ? JSON.parse(stored) : null
      const directions: Record<string, ChatSortDirection> = {}

      if (parsed && typeof parsed === 'object') {
        Object.entries(parsed).forEach(([tab, direction]) => {
          if (isSortDirection(direction)) directions[tab] = direction
        })
      }

      sortDirectionByTab.value = directions
    } catch {
      sortDirectionByTab.value = {}
    }
  }

  const getSortDirection = (tab: string): ChatSortDirection =>
    sortDirectionByTab.value[tab] ?? DEFAULT_SORT_DIRECTION

  const setSortDirection = (tab: string, direction: ChatSortDirection) => {
    sortDirectionByTab.value = { ...sortDirectionByTab.value, [tab]: direction }

    try {
      localStorage.setItem(sortStorageKey(), JSON.stringify(sortDirectionByTab.value))
    } catch {
      // Sin almacenamiento la preferencia dura lo que la sesion.
    }
  }

  const toggleSortDirection = (tab: string) => {
    setSortDirection(tab, getSortDirection(tab) === 'desc' ? 'asc' : 'desc')
  }

  const setActiveListTab = (tab: string) => {
    activeListTab.value = tab
  }

  /** Direccion de la pestana visible: la que ordena la lista y la que se pide al API. */
  const sortDirection = computed(() => getSortDirection(activeListTab.value))

  // Cambiar de agente en el mismo navegador trae sus preferencias.
  watch(currentUserId, loadSortDirection)

  // La lista se ordena por la llegada del chat (`created_at`, que es su
  // `start_date` y no cambia nunca), no por el ultimo mensaje: con actividad,
  // un chat saltaba de posicion cada vez que el cliente escribia, justo debajo
  // del cursor del agente. `last_message_time` queda solo como respaldo para un
  // chat que llegue sin fecha de inicio.
  const sortedChats = computed(() => {
    const factor = sortDirection.value === 'asc' ? 1 : -1

    return [...chats.value]
      .filter((chat) => ACTIVE_CHAT_STATUSES.includes(chat.status))
      .sort((a, b) => {
        const aTime = parseUtcDate(a.created_at || a.last_message_time || '').getTime()
        const bTime = parseUtcDate(b.created_at || b.last_message_time || '').getTime()

        // Empate (dos chats en el mismo segundo): el id desempata para que el
        // orden no baile entre renders.
        if (aTime === bTime) return a.chat_id.localeCompare(b.chat_id) * factor

        return (aTime - bTime) * factor
      })
  })

  // Cuantos de MIS chats tienen algo por leer. Antes sumaba los no leidos de
  // todos los chats activos, cola incluida: la insignia contaba conversaciones
  // que no son de nadie todavia.
  const unreadChatsCount = computed(
    () => chats.value.filter((chat) => isMyChat(chat) && chat.unread_count > 0).length,
  )

  const finishedChats = computed(() =>
    chats.value.filter((chat) => FINISHED_CHAT_STATUSES.includes(chat.status)),
  )

  const loadChatSignatures = () => {
    const stored = localStorage.getItem('whatsapp_chat_signatures')
    if (stored) {
      chatSignatureEnabled.value = JSON.parse(stored)
    }
  }

  const saveChatSignatures = () => {
    localStorage.setItem('whatsapp_chat_signatures', JSON.stringify(chatSignatureEnabled.value))
  }

  const toggleChatSignature = (chatId: string) => {
    chatSignatureEnabled.value[chatId] = !chatSignatureEnabled.value[chatId]
    saveChatSignatures()
  }

  const isChatSignatureEnabled = (chatId: string): boolean =>
    chatSignatureEnabled.value[chatId] || false

  const loadChatTranslations = () => {
    const stored = localStorage.getItem('whatsapp_chat_translations')
    if (!stored) return

    try {
      const parsed = JSON.parse(stored)
      if (!parsed || typeof parsed !== 'object') return

      chatTranslationSettings.value = Object.fromEntries(
        Object.entries(parsed).flatMap(([chatId, setting]) => {
          if (!setting || typeof setting !== 'object') return []
          const current = setting as Partial<ChatTranslationSetting> & { targetLanguage?: unknown }
          return [
            [
              chatId,
              {
                enabled: current.enabled === true,
                agentLanguage:
                  typeof current.agentLanguage === 'string'
                    ? current.agentLanguage
                    : typeof current.targetLanguage === 'string'
                      ? current.targetLanguage
                      : 'es',
                clientLanguage:
                  typeof current.clientLanguage === 'string' ? current.clientLanguage : 'en',
              },
            ],
          ]
        }),
      )
      saveChatTranslations()
    } catch {
      chatTranslationSettings.value = {}
    }
  }

  const saveChatTranslations = () => {
    localStorage.setItem(
      'whatsapp_chat_translations',
      JSON.stringify(chatTranslationSettings.value),
    )
  }

  const toggleChatTranslation = (chatId: string, agentLanguage = 'es', clientLanguage = 'en') => {
    const current = chatTranslationSettings.value[chatId]
    const nextEnabled = !current?.enabled
    chatTranslationSettings.value[chatId] = {
      enabled: nextEnabled,
      agentLanguage: current?.agentLanguage || agentLanguage,
      clientLanguage: current?.clientLanguage || clientLanguage,
    }
    saveChatTranslations()
    return nextEnabled
  }

  const isChatTranslationEnabled = (chatId: string): boolean =>
    chatTranslationSettings.value[chatId]?.enabled || false

  const getChatAgentLanguage = (chatId: string): string =>
    chatTranslationSettings.value[chatId]?.agentLanguage || 'es'

  const getChatClientLanguage = (chatId: string): string =>
    chatTranslationSettings.value[chatId]?.clientLanguage || 'en'

  const setChatTranslationEnabled = (
    chatId: string,
    enabled: boolean,
    agentLanguage = 'es',
    clientLanguage = 'en',
  ) => {
    const current = chatTranslationSettings.value[chatId]
    chatTranslationSettings.value[chatId] = {
      enabled,
      agentLanguage: current?.agentLanguage || agentLanguage,
      clientLanguage: current?.clientLanguage || clientLanguage,
    }
    saveChatTranslations()
  }

  const setChatTranslationLanguages = (
    chatId: string,
    changes: Partial<Pick<ChatTranslationSetting, 'agentLanguage' | 'clientLanguage'>>,
  ) => {
    const current = chatTranslationSettings.value[chatId]
    chatTranslationSettings.value[chatId] = {
      enabled: current?.enabled ?? false,
      agentLanguage: changes.agentLanguage || current?.agentLanguage || 'es',
      clientLanguage: changes.clientLanguage || current?.clientLanguage || 'en',
    }
    saveChatTranslations()
  }

  // Marca de agua por chat: cuando el estado local se adelanto —un evento del WS,
  // o una actualizacion de la propia UI—, en reloj del cliente. El resync la
  // compara contra el momento en que arranco y en que pidio cada tanda; si
  // el cambio ocurrio despues, el snapshot que trae esa tanda es mas viejo y
  // aplicarlo revertiria lo que el agente ya vio.
  //
  // Se usa hora local en los dos extremos a proposito: el timestamp del envelope
  // del WS es del reloj del servidor y GET /oper/chats no devuelve ninguno, asi
  // que compararlos seria cruzar dos relojes que pueden ir desfasados. No es
  // reactivo — nada lo renderiza — y solo se limpia al cerrar sesion: las
  // entradas de chats ya cerrados siguen haciendo falta durante un resync para
  // no readmitir un chat que el WS acaba de sacar.
  const chatLocalRevisions = new Map<string, number>()

  const markChatRevisedLocally = (chatId: string) => {
    chatLocalRevisions.set(chatId, Date.now())
  }

  const isChatRevisedSince = (chatId: string, since: number) =>
    (chatLocalRevisions.get(chatId) ?? 0) > since

  const withCampaignName = (chat: Chat): Chat => {
    if (chat.campaign_name) return chat

    const campaignName = campaignMap.value.get(chat.campaign_id)?.name
    return campaignName ? { ...chat, campaign_name: campaignName } : chat
  }

  const addChat = (chat: Chat, options?: { fromResync?: boolean }) => {
    const chatWithCampaignName = withCampaignName(chat)
    const existingIndex = chats.value.findIndex((c) => c.chat_id === chat.chat_id)
    if (existingIndex !== -1) {
      chats.value[existingIndex] = chatWithCampaignName
    } else {
      chats.value.push(chatWithCampaignName)
    }

    if (!options?.fromResync) markChatRevisedLocally(chat.chat_id)
  }

  const updateChat = (chatId: string, updates: Partial<Chat>) => {
    const chatIndex = chats.value.findIndex((chat) => chat.chat_id === chatId)
    if (chatIndex !== -1) {
      chats.value[chatIndex] = withCampaignName({ ...chats.value[chatIndex], ...updates })
      markChatRevisedLocally(chatId)
    }
  }

  const removeChat = (chatId: string) => {
    const index = chats.value.findIndex((chat) => chat.chat_id === chatId)
    if (index !== -1) {
      chats.value.splice(index, 1)
      markChatRevisedLocally(chatId)
    }
  }

  const selectChat = (chatId: string | null) => {
    selectedChatId.value = chatId
    if (chatId) {
      markChatAsRead(chatId)
    }
  }

  // Las dos marcan revision local: el conteo ahora tambien llega en el snapshot
  // de `GET /oper/chats`, y uno pedido antes de esta lectura revivria la insignia.
  const markChatAsRead = (chatId: string) => {
    const chat = chats.value.find((c) => c.chat_id === chatId)
    if (chat && chat.unread_count !== 0) {
      chat.unread_count = 0
      markChatRevisedLocally(chatId)
    }
  }

  const incrementUnreadCount = (chatId: string) => {
    const chat = chats.value.find((c) => c.chat_id === chatId)
    if (chat && selectedChatId.value !== chatId) {
      chat.unread_count++
      markChatRevisedLocally(chatId)
    }
  }

  const updateChatStatus = (chatId: string, status: ChatStatus) => {
    updateChat(chatId, { status })
  }

  const updateChatUser = (chatId: string, userId: string | null, userName?: string) => {
    updateChat(chatId, {
      user_id: userId,
      user_name: userName,
      assigned_at: userId ? new Date().toISOString() : undefined,
    })
  }

  const setHistoricalMessages = (chatId: string, historicalMessages: Chat['messages']) => {
    const chat = chats.value.find((item) => item.chat_id === chatId)
    if (!chat) return

    const historicalIds = new Set(historicalMessages.map((m) => m.id))
    // Preserve any live messages (received via WS) not yet in the historical response.
    // They arrived after the history request, so they always belong at the end.
    const liveNewMessages = chat.messages.filter((m) => !historicalIds.has(m.id))
    // El backend ya devuelve el historial ordenado por sesión + consecutivo. No se
    // reordena por `date`: el consecutivo se reinicia en cada sesión y `date` empata
    // a nivel de segundo, así que ordenar aquí rompe el orden correcto.
    chat.messages = [...historicalMessages, ...liveNewMessages]
  }

  const prependHistoricalMessages = (chatId: string, historicalMessages: Chat['messages']) => {
    const chat = chats.value.find((item) => item.chat_id === chatId)
    if (!chat || historicalMessages.length === 0) return

    const currentIds = new Set(chat.messages.map((message) => message.id))
    const olderMessages = historicalMessages.filter((message) => !currentIds.has(message.id))
    if (olderMessages.length === 0) return

    chat.messages = [...olderMessages, ...chat.messages]
  }

  const setCampaignMap = (campaigns: Map<string, IOptionsCampaign>) => {
    campaignMap.value = campaigns
    chats.value.forEach((chat) => {
      const campaign = campaigns.get(chat.campaign_id)
      if (campaign) {
        chat.campaign_name = campaign.name
      }
    })
  }

  const getCampaign = (campaignId: string) => campaignMap.value.get(campaignId)

  // El resync llega en tandas y solo al terminar se sabe que chats dejaron de
  // venir del backend. Se salva de la poda todo lo que no pueda darse por
  // obsoleto con certeza:
  //   - lo que no estaba cuando empezo (`knownChatIds`): entro despues, por WS.
  //   - lo que alguna tanda devolvio (`seenChatIds`).
  //   - lo que se adelanto durante el resync: un chat tomado a mitad de camino
  //     no sale en las tandas que ya se habian pedido, y podarlo lo borraria de
  //     la lista justo despues de que el agente lo tomara.
  const pruneStaleChats = (
    knownChatIds: Set<string>,
    seenChatIds: Set<string>,
    resyncStartedAt: number,
  ) => {
    fullSyncCount.value++

    const remainingChats = chats.value.filter(
      (chat) =>
        !knownChatIds.has(chat.chat_id) ||
        seenChatIds.has(chat.chat_id) ||
        isChatRevisedSince(chat.chat_id, resyncStartedAt),
    )
    if (remainingChats.length === chats.value.length) return

    chats.value = remainingChats
  }

  const clearChats = () => {
    chatLocalRevisions.clear()
    chats.value = []
    selectedChatId.value = null
    chatTimeoutClosures.value = {}
    chatBulkTemplateReleases.value = new Map()
  }

  const setChatTimeoutClosure = (
    chatId: string,
    data: { campaignId: string | null; botId: string | null } | null,
  ) => {
    if (data === null) {
      delete chatTimeoutClosures.value[chatId]
    } else {
      chatTimeoutClosures.value[chatId] = data
    }
  }

  const getChatTimeoutClosure = (chatId: string) => chatTimeoutClosures.value[chatId] ?? null

  const setChatBulkTemplateRelease = (chatId: string, closeReason: string | null) => {
    if (closeReason) {
      chatBulkTemplateReleases.value.set(chatId, closeReason)
    } else {
      chatBulkTemplateReleases.value.delete(chatId)
    }
  }

  const isChatReleasedByBulkTemplate = (chatId: string) =>
    chatBulkTemplateReleases.value.has(chatId)

  const getChatBulkTemplateReleaseReason = (chatId: string): string | null =>
    chatBulkTemplateReleases.value.get(chatId) ?? null

  const getChatsByStatus = (status: ChatStatus) =>
    chats.value.filter((chat) => chat.status === status)

  const getChatsByUserId = (userId: string) => chats.value.filter((chat) => chat.user_id === userId)

  const getActiveChatsByUser = (userId: string) =>
    chats.value.filter(
      (chat) => chat.user_id === userId && ACTIVE_CHAT_STATUSES.includes(chat.status),
    )

  loadChatSignatures()
  loadSortDirection()
  loadChatTranslations()

  return {
    // Estado
    chats,
    fullSyncCount,
    selectedChatId,
    currentUserId,
    currentUserRole,
    campaignMap,
    chatSignatureEnabled,
    chatTranslationSettings,
    chatTimeoutClosures,

    // Computed
    selectedChat,
    sortedChats,
    unreadChatsCount,
    finishedChats,

    // Orden de la lista
    sortDirection,
    activeListTab,
    getSortDirection,
    setSortDirection,
    toggleSortDirection,
    setActiveListTab,

    // Chat signatures
    toggleChatSignature,
    isChatSignatureEnabled,
    loadChatSignatures,
    saveChatSignatures,

    // Chat translations
    toggleChatTranslation,
    setChatTranslationEnabled,
    setChatTranslationLanguages,
    isChatTranslationEnabled,
    getChatAgentLanguage,
    getChatClientLanguage,
    loadChatTranslations,
    saveChatTranslations,

    // Chat management
    addChat,
    updateChat,
    removeChat,
    selectChat,
    markChatAsRead,
    incrementUnreadCount,
    updateChatStatus,
    updateChatUser,
    setHistoricalMessages,
    prependHistoricalMessages,

    // Campaign management
    setCampaignMap,
    getCampaign,

    // Timeout closures
    setChatTimeoutClosure,
    getChatTimeoutClosure,

    // Bulk template releases
    setChatBulkTemplateRelease,
    isChatReleasedByBulkTemplate,
    getChatBulkTemplateReleaseReason,

    // Resync
    isChatRevisedSince,

    // Bulk operations
    pruneStaleChats,
    clearChats,

    // Filters
    getChatsByStatus,
    getChatsByUserId,
    getActiveChatsByUser,
  }
})
